import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { createSlugWithId } from '../utils/slugUtils';
import { 
  BookOpenIcon, 
  CalendarIcon,
  AcademicCapIcon,
  PuzzlePieceIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
  TrophyIcon,
  StarIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import publicDeckService, { DeckSearchQuery, PublicDeck } from '../services/publicDeckService';
import publicQuizService, { QuizSearchQuery, PublicQuiz } from '../services/publicQuizService';
import publicMatchService, { MatchSearchQuery, PublicMatch } from '../services/publicMatchService';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';

const ITEMS_PER_PAGE = 24;

type ContentType = 'all' | 'decks' | 'quizzes' | 'matches';

const ContentSearcher: React.FC = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [decks, setDecks] = useState<PublicDeck[]>([]);
  const [quizzes, setQuizzes] = useState<PublicQuiz[]>([]);
  const [matches, setMatches] = useState<PublicMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedTags, setSelectedTags] = useState<string[]>(
    searchParams.get('tags')?.split(',').filter(Boolean) || []
  );
  const [contentType, setContentType] = useState<ContentType>(
    (searchParams.get('type') as ContentType) || 'all'
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1')
  );
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const searchContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const query = {
        q: searchQuery || undefined,
        tags: selectedTags.length > 0 ? selectedTags.join(',') : undefined,
        page: currentPage,
        limit: ITEMS_PER_PAGE
      };

      let totalCount = 0;
      let totalPagesCount = 1;

      if (contentType === 'all') {
        // Use unified search endpoint for "all" type with proper pagination
        const params = new URLSearchParams();
        params.append('type', 'all');
        if (query.q) params.append('q', query.q);
        if (query.tags) params.append('tags', query.tags);
        params.append('page', query.page.toString());
        params.append('limit', query.limit.toString());
        
        const response = await api.get(`/public/search?${params.toString()}`);
        const data = response.data;
        
        // Parse content from unified response
        const fetchedDecks: PublicDeck[] = [];
        const fetchedQuizzes: PublicQuiz[] = [];
        const fetchedMatches: PublicMatch[] = [];
        
        (data.content || []).forEach((item: any) => {
          if (item.contentType === 'deck') {
            fetchedDecks.push(item);
          } else if (item.contentType === 'quiz') {
            fetchedQuizzes.push(item);
          } else if (item.contentType === 'match') {
            fetchedMatches.push(item);
          }
        });
        
        setDecks(fetchedDecks);
        setQuizzes(fetchedQuizzes);
        setMatches(fetchedMatches);
        totalCount = data.total;
        totalPagesCount = data.totalPages;
      } else {
        // Search specific content type
        if (contentType === 'decks') {
          const deckResponse = await publicDeckService.searchDecks(query);
          setDecks(deckResponse.decks || []);
          setQuizzes([]);
          setMatches([]);
          totalCount = deckResponse.total;
          totalPagesCount = deckResponse.totalPages;
        } else if (contentType === 'quizzes') {
          const quizResponse = await publicQuizService.searchQuizzes(query);
          setDecks([]);
          setQuizzes(quizResponse.quizzes || []);
          setMatches([]);
          totalCount = quizResponse.total;
          totalPagesCount = quizResponse.totalPages;
        } else if (contentType === 'matches') {
          const matchResponse = await publicMatchService.searchMatches(query);
          setDecks([]);
          setQuizzes([]);
          setMatches(matchResponse.matches || []);
          totalCount = matchResponse.total;
          totalPagesCount = matchResponse.totalPages;
        }
      }

      setTotal(totalCount);
      setTotalPages(totalPagesCount);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to search content');
      setDecks([]);
      setQuizzes([]);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedTags, contentType, currentPage]);

  // Update URL params when search changes
  const updateSearchParams = useCallback(() => {
    const newParams = new URLSearchParams();
    
    if (searchQuery) newParams.set('q', searchQuery);
    if (selectedTags.length > 0) newParams.set('tags', selectedTags.join(','));
    if (contentType !== 'all') newParams.set('type', contentType);
    if (currentPage > 1) newParams.set('page', currentPage.toString());
    
    setSearchParams(newParams, { replace: true });
  }, [searchQuery, selectedTags, contentType, currentPage, setSearchParams]);

  // Perform search
  useEffect(() => {
    updateSearchParams();
    searchContent();
  }, [searchQuery, selectedTags, contentType, currentPage, searchContent, updateSearchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
    setContentType('all');
    setCurrentPage(1);
  };

  const handleBack = () => {
    const referrer = document.referrer;

    if (referrer) {
      try {
        const referrerUrl = new URL(referrer);
        if (referrerUrl.origin === window.location.origin) {
          navigate(referrerUrl.pathname + referrerUrl.search);
          return;
        }
      } catch {
        // Ignore invalid referrer values and fall back to browser history or home.
      }
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Explore Conteúdo de Aprendizado - Flashcards, Quizzes e Jogos | Upmindy</title>
        <meta name="description" content="Descubra flashcards, quizzes e jogos de memória de alta qualidade criados por especialistas da Upmindy. Domine novas habilidades com materiais de aprendizagem estruturados. Usuários gratuitos podem copiar qualquer quantidade de conteúdo." />
        <meta name="keywords" content="flashcards, quizzes, jogos de memória, aprendizado, estudo, educação, memorização, cursos, conhecimento, Upmindy" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="Upmindy" />
        <link rel="canonical" href="https://upmindy.com/search" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Explore Conteúdo de Aprendizado - Flashcards, Quizzes e Jogos | Upmindy" />
        <meta property="og:description" content="Descubra flashcards, quizzes e jogos de memória de alta qualidade criados por especialistas da Upmindy. Usuários gratuitos podem copiar qualquer quantidade de conteúdo." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://upmindy.com/search" />
        <meta property="og:site_name" content="Upmindy" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Explore Conteúdo de Aprendizado - Flashcards, Quizzes e Jogos | Upmindy" />
        <meta name="twitter:description" content="Descubra flashcards, quizzes e jogos de memória de alta qualidade criados por especialistas da Upmindy. Usuários gratuitos podem copiar qualquer quantidade de conteúdo." />
      </Helmet>

      {/* Header */}
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          {/* Back Button */}
          <div className="mb-4 sm:mb-6">
            <button
              type="button"
              onClick={handleBack}
              className={`inline-flex items-center text-sm font-medium transition-colors ${
                isDark 
                  ? 'text-gray-400 hover:text-white' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Voltar
            </button>
          </div>

          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
              <span className="text-blue-600">Explore</span> Conteúdo de Aprendizado
            </h1>
            <p className={`text-sm sm:text-base lg:text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-3xl mx-auto mb-4 px-4`}>
              Descubra flashcards, quizzes e jogos de memória de alta qualidade criados e compartilhados.
            </p>
          </div>

          {/* Content Type Tabs */}
          <div className="mt-6 sm:mt-8 flex justify-center overflow-x-auto pb-2 sm:pb-0">
            <div className={`inline-flex rounded-lg p-1 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} min-w-max`}>
              {[
                { key: 'all', label: 'Todos', icon: AcademicCapIcon },
                { key: 'decks', label: 'Flashcards', icon: BookOpenIcon },
                { key: 'quizzes', label: 'Quizzes', icon: QuestionMarkCircleIcon },
                { key: 'matches', label: 'Matches', icon: PuzzlePieceIcon }
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => {
                    setContentType(key as ContentType);
                    setCurrentPage(1);
                  }}
                  className={`flex items-center px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                    contentType === key
                      ? 'bg-blue-600 text-white'
                      : isDark
                        ? 'text-gray-300 hover:text-white hover:bg-gray-600'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                  }`}
                >
                  <Icon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Search Form */}
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            onSubmit={() => setCurrentPage(1)}
            placeholder="Pesquise por nome, descrição ou tags..."
            className="mt-6 sm:mt-8 max-w-2xl mx-auto"
          />

          {/* Active Filters */}
          {(searchQuery || selectedTags.length > 0 || contentType !== 'all') && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 px-4">
              <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Filtros:
              </span>
              {contentType !== 'all' && (
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                  {contentType === 'decks' ? 'Flashcards' : contentType === 'quizzes' ? 'Quizzes' : 'Matches'}
                </span>
              )}
              {searchQuery && (
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                  "{searchQuery.length > 20 ? searchQuery.substring(0, 20) + '...' : searchQuery}"
                </span>
              )}
              {selectedTags.map(tag => (
                <span key={tag} className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                  {tag}
                </span>
              ))}
              <button
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 text-xs sm:text-sm underline"
              >
                Limpar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">{error}</div>
            <button
              onClick={searchContent}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Tentar Novamente
            </button>
          </div>
        )}

        {/* Content Grid */}
        {!loading && !error && (
          <>
            {/* Combined All Content */}
            {(() => {
              // Combine all content into a single array
              type ContentItem = {
                id: string;
                type: 'deck' | 'quiz' | 'match';
                title: string;
                description?: string;
                tags: string[];
                createdAt: string;
                link: string;
                metadata: any;
              };

              const allContent: ContentItem[] = [];

              // Add decks
              decks.forEach(deck => {
                allContent.push({
                  id: deck._id,
                  type: 'deck',
                  title: deck.name,
                  description: deck.description,
                  tags: deck.tags,
                  createdAt: deck.createdAt,
                  link: `/flashcards/${createSlugWithId(deck.name, deck._id)}`,
                  metadata: { cardCount: deck.cardCount }
                });
              });

              // Add quizzes
              quizzes.forEach(quiz => {
                allContent.push({
                  id: quiz._id,
                  type: 'quiz',
                  title: quiz.title,
                  description: quiz.description,
                  tags: quiz.tags,
                  createdAt: quiz.createdAt,
                  link: `/quizzes/${createSlugWithId(quiz.title, quiz._id)}`,
                  metadata: { questionsCount: quiz.questionsCount, bestScore: quiz.bestScore }
                });
              });

              // Add matches
              matches.forEach(match => {
                allContent.push({
                  id: match._id,
                  type: 'match',
                  title: match.title,
                  description: match.description,
                  tags: match.tags,
                  createdAt: match.createdAt,
                  link: `/matches/${createSlugWithId(match.title, match._id)}`,
                  metadata: { pairsCount: match.pairsCount, bestTime: match.bestTime }
                });
              });

              const getContentConfig = (type: 'deck' | 'quiz' | 'match') => {
                switch (type) {
                  case 'deck':
                    return {
                      icon: BookOpenIcon,
                      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
                      textColor: 'text-blue-600 dark:text-blue-400',
                      hoverBorder: 'hover:border-blue-300 dark:hover:border-blue-600',
                      label: 'Flashcard',
                    };
                  case 'quiz':
                    return {
                      icon: QuestionMarkCircleIcon,
                      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
                      textColor: 'text-purple-600 dark:text-purple-400',
                      hoverBorder: 'hover:border-purple-300 dark:hover:border-purple-600',
                      label: 'Quiz',
                    };
                  case 'match':
                    return {
                      icon: PuzzlePieceIcon,
                      bgColor: 'bg-pink-100 dark:bg-pink-900/30',
                      textColor: 'text-pink-600 dark:text-pink-400',
                      hoverBorder: 'hover:border-pink-300 dark:hover:border-pink-600',
                      label: 'Match',
                    };
                }
              };

              return allContent.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                  {allContent.map((item) => {
                    const config = getContentConfig(item.type);
                    const Icon = config.icon;

                    return (
                      <Link
                        key={item.id}
                        to={item.link}
                        className={`block p-6 rounded-lg border transition-all hover:shadow-lg ${
                          isDark 
                            ? 'bg-gray-800 border-gray-700 hover:border-gray-600' 
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <Icon className={`h-8 w-8 ${config.textColor} flex-shrink-0`} />
                          <div className="flex flex-wrap gap-1 ml-2">
                            {item.tags.slice(0, 2).map(tag => (
                              <span
                                key={tag}
                                className={`px-2 py-1 text-xs rounded ${
                                  isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {tag}
                              </span>
                            ))}
                            {item.tags.length > 2 && (
                              <span className={`px-2 py-1 text-xs rounded ${
                                isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                                +{item.tags.length - 2}
                              </span>
                            )}
                          </div>
                        </div>

                        <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                          {item.title}
                        </h3>

                        <p className={`text-sm mb-4 line-clamp-3 ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {item.description || 'Nenhuma descrição disponível'}
                        </p>

                        <div className="space-y-2">
                          {item.type === 'deck' && (
                            <>
                              <div className="flex items-center text-sm">
                                <AcademicCapIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                                  {item.metadata.cardCount} cards
                                </span>
                              </div>
                              <div className="flex items-center text-sm">
                                <CalendarIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                                  Criado em {formatDate(item.createdAt)}
                                </span>
                              </div>
                            </>
                          )}
                          {item.type === 'quiz' && (
                            <>
                              <div className="flex items-center text-sm">
                                <QuestionMarkCircleIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                                  {item.metadata.questionsCount} perguntas
                                </span>
                              </div>
                              <div className="flex items-center text-sm">
                                <CalendarIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                                  Criado em {formatDate(item.createdAt)}
                                </span>
                              </div>
                            </>
                          )}
                          {item.type === 'match' && (
                            <>
                              <div className="flex items-center text-sm">
                                <PuzzlePieceIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                                  {item.metadata.pairsCount} pares
                                </span>
                              </div>
                              <div className="flex items-center text-sm">
                                <CalendarIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                                  Criado em {formatDate(item.createdAt)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : null;
            })()}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <nav className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded-md ${
                      currentPage === 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : isDark
                          ? 'bg-gray-700 text-white hover:bg-gray-600'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border'
                    }`}
                  >
                    Anterior
                  </button>

                  {(() => {
                    const pages: number[] = [];
                    if (totalPages <= 5) {
                      for (let p = 1; p <= totalPages; p++) pages.push(p);
                    } else if (currentPage <= 3) {
                      pages.push(1, 2, 3, 4, 5);
                    } else if (currentPage >= totalPages - 2) {
                      pages.push(totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                    } else {
                      pages.push(currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2);
                    }
                    return pages.map(pageNum => (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 rounded-md ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : isDark
                              ? 'bg-gray-700 text-white hover:bg-gray-600'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ));
                  })()}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 rounded-md ${
                      currentPage === totalPages
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : isDark
                          ? 'bg-gray-700 text-white hover:bg-gray-600'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border'
                    }`}
                  >
                    Próximo
                  </button>
                </nav>
              </div>
            )}

            {/* Empty State */}
            {decks.length === 0 && quizzes.length === 0 && matches.length === 0 && !loading && (
              <div className="text-center py-12">
                <AcademicCapIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhum conteúdo encontrado</h3>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  Tente ajustar seus critérios de busca ou navegue pelos diferentes tipos de conteúdo.
                </p>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Limpar Filtros
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContentSearcher;